<template src="./Tickets.html"></template>

<script>
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import { getAllTickets, deleteTicket, updateTicket } from '../../services'
import { ticketStatusVal, ticketPriorityVal } from '../../utils/constants'
import { getTicketPriority, getTicketPriorityColor, getTicketStatus, getTicketStatusColor } from '../../utils/computeds'
import { TicketModuleAccess } from '../../utils/roles'
import { formatDueDate } from '../../utils/dueDate'
import TicketAssigneeSelect from '../../components/Ticket/TicketAssigneeSelect.vue'
import ProjectSelect from '../../components/Project/ProjectSelect.vue'
import TicketCard from '../../components/Ticket/TicketCard.vue'
import TicketCounters from '../../components/Ticket/TicketCounters.vue'
import TicketFormDialog from '../../components/Ticket/TicketFormDialog.vue'
import TicketDetailDialog from '../../components/Ticket/TicketDetailDialog.vue'

const emptyBoard = () => ({
  BACKLOG: [], TODO: [], IN_PROGRESS: [], DONE: [], CANCELLED: []
})

// Filters that can arrive in (and are written back to) the URL, so a link such as "Ver todos en el
// tablero" from "Mis pendientes" opens the board already filtered, and a reload keeps the view.
const URL_FILTERS = ['dueDateFrom', 'dueDateTo', 'status', 'priority', 'projectId']

export default {
  name: 'Tickets',
  components: {
    draggable, TicketAssigneeSelect, ProjectSelect, TicketCard, TicketCounters, TicketFormDialog,
    TicketDetailDialog
  },
  data: () => ({
    viewMode: 'board',
    loading: false,
    board: emptyBoard(),
    statusOptions: ticketStatusVal,
    priorityOptions: ticketPriorityVal,
    filters: {
      dueDateFrom: null,
      dueDateTo: null,
      status: null,
      priority: null,
      // The picker is multi-select, so this is a list: the board shows the tickets of anyone picked.
      assigneeIds: [],
      projectId: null,
      // "Solo mis tickets": filters by the current user without needing a project picked first,
      // which the assignee dropdown requires because it lists one project's members.
      onlyMine: false
    },
    initialized: false,
    createDialogOpen: false,
    detailDialogOpen: false,
    selectedTicketId: null,
    // Estado de los v-menu de los date-pickers de los filtros (ver nota en Tickets.html: se usa
    // v-menu + v-date-picker en vez de <input type="date"> nativo porque Safari muestra la fecha
    // de hoy como "placeholder" en un campo vacío, dando la impresión de que ya tiene un valor).
    dueDateFromMenu: false,
    dueDateToMenu: false,
    headers: [
      { text: 'Título', value: 'title' },
      { text: 'Proyecto', value: 'project' },
      { text: 'Estado', value: 'status' },
      { text: 'Prioridad', value: 'priority' },
      { text: 'Asignado', value: 'assignedTo' },
      { text: 'Fecha límite', value: 'dueDate' }
    ]
  }),
  computed: {
    ...mapGetters({
      user: 'getUser'
    }),
    getTicketPriority,
    getTicketPriorityColor,
    getTicketStatus,
    getTicketStatusColor,
    flatTickets () {
      return Object.values(this.board).flat()
    }
  },
  watch: {
    user: {
      immediate: true,
      handler (user) {
        if (!user || this.initialized) return
        this.initialized = true

        if (!TicketModuleAccess()(user.rol)) {
          this.$router.replace('/')
          return
        }
        const ticketId = this.$route?.query?.ticketId
        this.applyUrlFilters()
        this.fetchTickets().then(() => {
          if (ticketId) this.openDetail({ id: ticketId })
        })
      }
    }
  },
  methods: {
    applyUrlFilters () {
      const query = this.$route?.query || {}
      URL_FILTERS.forEach((key) => {
        if (query[key]) this.filters[key] = query[key]
      })
      this.filters.onlyMine = query.onlyMine === '1'
    },
    // Mirrors the applied filters into the URL. `replace`, not `push`: refining a filter should not
    // add a history entry per keystroke.
    syncUrlFilters () {
      if (!this.$route) return
      const query = {}
      URL_FILTERS.forEach((key) => {
        if (this.filters[key]) query[key] = this.filters[key]
      })
      if (this.filters.onlyMine) query.onlyMine = '1'
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return
      this.$router.replace({ query }).catch(() => {})
    },
    buildParams () {
      const { onlyMine, ...rest } = this.filters
      const params = Object.fromEntries(Object.entries(rest).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0
        return value !== null && value !== ''
      }))
      if (onlyMine && this.user) params.assigneeIds = [this.user.id]
      return params
    },
    onOnlyMineChange (onlyMine) {
      // The two ways of filtering by person are exclusive: switching to "mine" clears a person picked
      // in the dropdown, which is then disabled while the switch is on.
      if (onlyMine) this.filters.assigneeIds = []
      this.fetchTickets()
    },
    formatDueDate,
    async fetchTickets () {
      this.loading = true
      try {
        // Every matching ticket, not a first page: the board is the whole-project view.
        const data = await getAllTickets(this.buildParams())
        this.syncUrlFilters()
        const board = emptyBoard()
        data.forEach((ticket) => {
          if (!board[ticket.status]) board[ticket.status] = []
          board[ticket.status].push(ticket)
        })
        this.board = board
        if (this.$refs.counters) this.$refs.counters.refresh()
      } catch (err) {
        console.error('Error fetching tickets:', err)
        // The board has to be emptied: leaving the previous result on screen makes a failed request
        // look like a filter that was silently ignored.
        this.board = emptyBoard()
        this.$swal({
          text: err.message ?? 'No se pudieron cargar los tickets, intenta más tarde.',
          buttons: false,
          icon: 'error',
          timer: 3000
        })
      } finally {
        this.loading = false
      }
    },
    // The assignee filter is fed by the chosen project's members, so on a project change the
    // selected person no longer makes sense.
    onProjectFilterChange () {
      this.filters.assigneeIds = []
      this.fetchTickets()
    },
    openCreate () {
      this.createDialogOpen = true
    },
    openDetail (ticket) {
      this.selectedTicketId = ticket.id
      this.detailDialogOpen = true
    },
    async moveTicketToStatus (ticket, newStatus) {
      if (!newStatus || ticket.status === newStatus) return
      const oldStatus = ticket.status
      try {
        await updateTicket(ticket.id, { status: newStatus })
        const idx = this.board[oldStatus].findIndex((t) => t.id === ticket.id)
        if (idx !== -1) this.board[oldStatus].splice(idx, 1)
        ticket.status = newStatus
        this.board[newStatus].push(ticket)
      } catch (err) {
        console.error('Error updating ticket status:', err)
      }
    },
    onColumnChange (status, event) {
      if (event.added) {
        const ticket = event.added.element
        ticket.status = status
        updateTicket(ticket.id, { status }).catch((err) => console.error('Error updating ticket status (drag):', err))
      }
    },
    confirmDelete (ticket) {
      this.$swal({
        title: '¿Está seguro de eliminar el ticket?',
        icon: 'warning',
        buttons: true,
        dangerMode: true
      }).then((value) => {
        if (value) { this.removeTicket(ticket) }
      })
    },
    async removeTicket (ticket) {
      try {
        await deleteTicket(ticket.id)
        this.$swal({ text: 'Ticket eliminado.', buttons: false, icon: 'success', timer: 2500 })
        this.fetchTickets()
      } catch (err) {
        console.error('Error deleting ticket:', err)
        this.$swal({
          text: err.message ?? 'Ocurrió un error, intenta más tarde.', buttons: false, icon: 'error', timer: 3000
        })
      }
    }
  }
}
</script>

<style lang="scss" src="./Tickets.scss" scoped></style>
